import type { Node } from "@xyflow/react";
import {
    type BucketSelection,
    type BuildFacetGraphParams,
    type ExplodeFlow,
    type ExplodeNodeData,
    NodeRole,
    type OverlayData,
} from "../../types";
import {
    COLUMN_GAP,
    columnsForAspect,
    GRID_TOP,
    graphNode,
    NODE_WIDTH,
    OVERLAY_PAGE_SIZE,
    ROOT_Y,
    ROW_GAP,
    STAGE_Z,
    titleEdge,
    valueEdge,
} from "./graphLayout";

export const buildOverlay = ({
    overlay,
    params,
}: {
    overlay: OverlayData;
    params: BuildFacetGraphParams;
}): ExplodeFlow => {
    const { categoryField, categoryLabel, rootLabel, selected, categories } = params;
    const start = overlay.page * OVERLAY_PAGE_SIZE;
    const pageBuckets = overlay.buckets.slice(start, start + OVERLAY_PAGE_SIZE);
    const remaining = overlay.buckets.length - (start + pageBuckets.length);
    const target = { field: overlay.field, label: overlay.label };

    const cellCount = pageBuckets.length + (remaining > 0 ? 1 : 0) + (overlay.page > 0 ? 1 : 0);
    const columns = columnsForAspect(cellCount || 1);
    const stepX = NODE_WIDTH + COLUMN_GAP;
    const cellX = (index: number) => (index % columns) * stepX;
    const cellY = (index: number) => GRID_TOP + Math.floor(index / columns) * ROW_GAP;

    const selectionFor = (value: string): BucketSelection[] =>
        overlay.field === categoryField || !selected
            ? [{ field: overlay.field, value }]
            : [selected, { field: overlay.field, value }];

    const valueNodes = pageBuckets.map((bucket, index) =>
        graphNode(
            `ovv-${index}`,
            cellX(index),
            cellY(index),
            {
                label: bucket.value,
                count: bucket.count,
                role: NodeRole.GROUP,
                selection: selectionFor(bucket.value),
            },
            STAGE_Z,
        ),
    );

    const nextNode: Node<ExplodeNodeData>[] =
        remaining > 0
            ? [
                  graphNode(
                      "ov-next",
                      cellX(pageBuckets.length),
                      cellY(pageBuckets.length),
                      {
                          label: `…next ${Math.min(OVERLAY_PAGE_SIZE, remaining)} (${remaining.toLocaleString()} left)`,
                          role: NodeRole.GROUP,
                          selection: [],
                          muted: true,
                          overlay: target,
                          overlayPage: overlay.page + 1,
                      },
                      STAGE_Z,
                  ),
              ]
            : [];

    const backIndex = pageBuckets.length + nextNode.length;
    const backNode: Node<ExplodeNodeData>[] =
        overlay.page > 0
            ? [
                  graphNode(
                      "ov-back",
                      cellX(backIndex),
                      cellY(backIndex),
                      {
                          label: "…back",
                          role: NodeRole.GROUP,
                          selection: [],
                          muted: true,
                          overlay: target,
                          overlayPage: overlay.page - 1,
                      },
                      STAGE_Z,
                  ),
              ]
            : [];

    const gridWidth = Math.min(cellCount || 1, columns) * stepX - COLUMN_GAP;
    const headerX = (gridWidth - NODE_WIDTH) / 2;
    const headerNode = graphNode(
        "ov-hdr",
        headerX,
        ROOT_Y,
        {
            label: overlay.label,
            role: NodeRole.TITLE,
            title: true,
            focused: true,
            count: overlay.buckets.length,
            selection: [],
        },
        STAGE_Z,
    );

    const chainValue = selected && overlay.field !== categoryField ? selected : undefined;
    const chainLength = chainValue ? 2 : 1;
    const rootChainNode = graphNode(
        "ov-root",
        headerX - chainLength * stepX,
        ROOT_Y,
        {
            label: rootLabel,
            role: NodeRole.ROOT,
            selection: [],
        },
        STAGE_Z,
    );
    const selectedChainNode: Node<ExplodeNodeData>[] = chainValue
        ? [
              graphNode(
                  "ov-sel",
                  headerX - stepX,
                  ROOT_Y,
                  {
                      label: chainValue.value,
                      caption: categoryLabel,
                      count: categories.find((bucket) => bucket.value === chainValue.value)?.count,
                      role: NodeRole.GROUP,
                      focused: true,
                      selection: [chainValue],
                  },
                  STAGE_Z,
              ),
          ]
        : [];

    const chainEdges = (
        chainValue
            ? [titleEdge("ov-root", "ov-sel", "e-ovc-0"), titleEdge("ov-sel", "ov-hdr", "e-ovc-1")]
            : [titleEdge("ov-root", "ov-hdr", "e-ovc-0")]
    ).map((edge) => ({ ...edge, zIndex: STAGE_Z - 1 }));

    const spiderEdges = valueNodes.map((valueNode, index) => ({
        ...valueEdge("ov-hdr", valueNode.id, `e-ov-${index}`),
        zIndex: STAGE_Z - 1,
    }));

    return {
        nodes: [
            rootChainNode,
            ...selectedChainNode,
            headerNode,
            ...valueNodes,
            ...nextNode,
            ...backNode,
        ],
        edges: [...chainEdges, ...spiderEdges],
    };
};
